/**
 * The VAT number by the supplier's country (board 1.8, report §3.1 «foreign suppliers», contract
 * `previewSupplierVat`): the EU is checked with VIES; the United Kingdom and Switzerland have the
 * field ready but no check yet; elsewhere the number is optional and not checked.
 */
export type VatRule = 'vies' | 'uk' | 'ch' | 'optional';

/** The VIES member states, as ISO 3166-1 codes (Greece is GR here, «EL» in VIES). */
export const VIES_COUNTRIES: ReadonlySet<string> = new Set(
  'AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK'.split(' '),
);

const EURO_AREA: ReadonlySet<string> = new Set(
  'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK AD MC SM VA ME XK'.split(' '),
);

const CURRENCY: Readonly<Record<string, string>> = {
  GB: 'GBP',
  CH: 'CHF',
  LI: 'CHF',
  US: 'USD',
  SE: 'SEK',
  DK: 'DKK',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  RO: 'RON',
  BG: 'BGN',
  NO: 'NOK',
  JP: 'JPY',
};

export function vatRule(country: string): VatRule {
  if (VIES_COUNTRIES.has(country)) return 'vies';
  if (country === 'GB') return 'uk';
  if (country === 'CH') return 'ch';
  return 'optional';
}

/** The usual currency suggested for a supplier of that country, if the console knows it. */
export function suggestedCurrency(country: string): string | null {
  if (EURO_AREA.has(country)) return 'EUR';
  return CURRENCY[country] ?? null;
}

/**
 * A VAT number as typed, in the contract's one form: letters and digits, upper-case, the country
 * prefix split off into `vatCountry` («DE 123456789» is DE + 123456789, Greece's «EL» is GR). A
 * Swiss IDI keeps its «CHE»: it is part of the number. Without a prefix, the supplier's country.
 */
export function splitVat(
  typed: string,
  country: string,
): { readonly vatCountry: string; readonly vat: string } | null {
  const clean = typed.replace(/[^0-9a-z]/gi, '').toUpperCase();
  if (clean === '') return null;
  if (/^CHE\d/.test(clean)) return { vatCountry: 'CH', vat: clean };
  const prefix = clean.slice(0, 2);
  // A known prefix only: Spain's and others' numbers may start with a letter after it (ESB…).
  if (/^[A-Z]{2}$/.test(prefix) && clean.length > 2) {
    if (prefix === 'EL') return { vatCountry: 'GR', vat: clean.slice(2) };
    if (VIES_COUNTRIES.has(prefix) || prefix === 'GB' || prefix === 'XI') {
      return { vatCountry: prefix, vat: clean.slice(2) };
    }
  }
  return { vatCountry: country, vat: clean };
}
